import {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  marketsForScope,
  type AdjustmentReasonCode,
  type AuditLogEntry,
  type BalanceLedgerEntry,
  type Currency,
  type Market,
  type User,
} from '../dataProvider/types';

/**
 * The Manual balance adjustments query/aggregation module (D-balance-adjustment, the
 * Phase 5 capstone) - the ONE place the manual-adjustment ledger is shaped for the
 * oversight view. Same seam idiom as the chargebacks worklist: v1 aggregates
 * client-side from the mock provider, every selector lives here, and the real-data
 * migration swaps this module for a server-side endpoint without the surface changing.
 *
 * It reads the SHARED signed ledger and keeps ONLY the manual_adjustment entries (the
 * chargeback reversals are the chargebacks surface's concern). Each row carries the
 * member, the SIGNED amount (a credit positive, a debit negative), the reason code and
 * note, when it happened, and the acting admin joined from the audit trail (the ledger
 * entry itself records no actor; the audit floor does).
 *
 * REGION: OPERATIONAL and SWITCHER-SCOPED (D-balance-adjustment), NOT investigative-
 * global. A wallet is per-member and single-market, so the ledger view follows the
 * region switcher scope via marketsForScope, exactly like the chargebacks and cashout
 * queues. A single-region super_admin sees only their market's adjustments.
 */

export interface AdjustmentRow {
  /** The ledger entry id (the row id). */
  id: number;
  userId: number;
  memberName: string;
  market: Market;
  currency: Currency;
  /** SIGNED: a credit is positive, a debit negative. */
  amount: number;
  reasonCode: AdjustmentReasonCode;
  reason: string;
  createdAt: string;
  /** The acting admin's email, joined from the audit trail, or null for a seeded
   *  entry that predates any recorded action. */
  actor: string | null;
}

export interface AdjustmentsView {
  entries: AdjustmentRow[];
}

export interface AdjustmentsInput {
  ledger: BalanceLedgerEntry[];
  users: User[];
  /** The audit trail, read to attribute each adjustment to its acting admin. */
  audit: AuditLogEntry[];
}

export interface AdjustmentsOptions {
  /** Access scope (UK | US | GLOBAL) from the region switcher. */
  scope: string;
}

/** The markets a scope renders, kept to the operating markets (the chargebacks pattern). */
const marketsFor = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

const newestFirst = (a: AdjustmentRow, b: AdjustmentRow): number =>
  b.createdAt.localeCompare(a.createdAt) || b.id - a.id;

export function buildAdjustmentsView(
  input: AdjustmentsInput,
  opts: AdjustmentsOptions,
): AdjustmentsView {
  const markets = marketsFor(opts.scope);
  const usersById = new Map(input.users.map((u) => [u.id, u]));
  // The acting admin lives in the audit trail, not the ledger entry: map each created
  // balanceLedger record to the actor who created it.
  const actorByEntryId = new Map<number, string>();
  for (const e of input.audit) {
    if (e.resource === 'balanceLedger' && e.action === 'create') {
      actorByEntryId.set(Number(e.recordId), e.actorEmail);
    }
  }

  const entries: AdjustmentRow[] = input.ledger
    .filter((e) => e.kind === 'manual_adjustment' && markets.includes(e.market))
    .map((e) => {
      const user = usersById.get(e.userId);
      return {
        id: e.id,
        userId: e.userId,
        memberName: user?.name ?? '',
        market: e.market,
        currency: CURRENCY_BY_MARKET[e.market],
        amount: Number(e.amount),
        reasonCode: e.reasonCode as AdjustmentReasonCode,
        reason: e.reason,
        createdAt: e.createdAt,
        actor: actorByEntryId.get(e.id) ?? null,
      };
    })
    .sort(newestFirst);

  return { entries };
}
